import React, { useContext, useEffect, useId, useState } from "react";
import { FINAL_STRIKE_EVENT, STRIKE_BUILDUP_MS } from "../events";

/*
 * Halloween: "Here's Johnny!"-memen i hjulets mitt, med texten
 * "JAG ÄR OCKSÅ BJUDEN!". Visas först när hjulet har snurrat klart.
 *
 * Bilden hämtas från public/holidays/ i den här ordningen:
 *   shining-heres-johnny.gif   (den animerade memen)
 *   heres-johnny.gif
 *   heres-johnny.jpg
 * så visas den, beskuren till en cirkel med meme-texten ovanpå. Finns
 * ingen bild används den tecknade varianten nedan som reserv.
 *
 * Reserven: en hyllning till skräckfilmsklassikerns mest kända scen.
 * En sönderhuggen trädörr, en yxa som sitter kvar i virket och ett galet
 * flinande ansikte som tittar in genom hålet – "JAG ÄR OCKSÅ BJUDEN!".
 * Egen tecknad figur, ingen filmbild.
 */

type IconProps = React.SVGProps<SVGSVGElement>;

/**
 * Ikonen ritas i en ruta på 200×200 men får en tom marginal runt om. Gifarna
 * är större än rutan (för beskärningen) och skars förut av vid rutans kant.
 * Med skärmskalning (t.ex. 125 %) läckte då en pixelrad av gifen ut längs
 * högra och nedre kanten, utanför den runda klippningen. Med marginalen når
 * ingen bild kanten. Hjulet förstorar ikonen lika mycket (WHEEL_REVEAL_SCALE).
 */
export const ICON_PAD = 70;
const ICON_VIEWBOX = `${-ICON_PAD} ${-ICON_PAD} ${200 + 2 * ICON_PAD} ${200 + 2 * ICON_PAD}`;

const HOLE =
  "M62 58 L 74 50 L 80 60 L 92 44 L 100 56 L 112 42 L 118 58 L 132 50 L 136 66 L 148 70 L 140 84 L 150 96 L 138 104 L 144 120 L 128 120 L 124 134 L 110 126 L 100 138 L 92 124 L 78 132 L 74 118 L 58 120 L 64 104 L 52 96 L 62 86 L 52 74 Z";

const HeresJohnnyCartoon: React.FC<IconProps> = props => {
  const id = useId().replace(/:/g, "");
  const u = (n: string) => `url(#${n}-${id})`;
  return (
    <svg viewBox={ICON_VIEWBOX} overflow="hidden" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g className="wheel-reveal">
      <defs>
        <clipPath id={`round-${id}`}>
          <circle cx="100" cy="100" r="96" />
        </clipPath>
        <clipPath id={`hole-${id}`}>
          <path d={HOLE} />
        </clipPath>
        <linearGradient id={`wood-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c67a3c" />
          <stop offset="55%" stopColor="#9a5424" />
          <stop offset="100%" stopColor="#5e2e12" />
        </linearGradient>
        <radialGradient id={`dark-${id}`} cx="50%" cy="40%" r="70%">
          <stop offset="0%" stopColor="#3a2014" />
          <stop offset="100%" stopColor="#0c0503" />
        </radialGradient>
        <radialGradient id={`skin-${id}`} cx="42%" cy="34%" r="72%">
          <stop offset="0%" stopColor="#ffe0c8" />
          <stop offset="55%" stopColor="#f0b48e" />
          <stop offset="100%" stopColor="#b8704e" />
        </radialGradient>
        <linearGradient id={`steel-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f4f6fa" />
          <stop offset="50%" stopColor="#a9b0bd" />
          <stop offset="100%" stopColor="#5a616e" />
        </linearGradient>
        <radialGradient id={`light-${id}`} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#ffd9a0" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ff9a40" stopOpacity="0" />
        </radialGradient>
        <filter id={`soft-${id}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
      </defs>

      <g clipPath={`url(#round-${id})`}>
        {/* Dörren: plankor med ådring och en ram */}
        <rect x="0" y="0" width="200" height="200" fill={u("wood")} />
        {[40, 80, 120, 160].map(x => (
          <path key={x} d={`M${x} 0 V 200`} stroke="#4a220c" strokeWidth="2.4" opacity="0.7" />
        ))}
        <g stroke="#6a3414" strokeWidth="1.2" fill="none" opacity="0.55">
          <path d="M14 0 C 18 40, 10 80, 16 120 C 20 150, 14 180, 18 200" />
          <path d="M58 0 C 54 30, 62 70, 56 110" />
          <path d="M96 160 C 100 180, 94 190, 98 200" />
          <path d="M140 0 C 136 30, 144 60, 138 90" />
          <path d="M178 40 C 182 80, 174 120, 180 170" />
        </g>
        <rect x="18" y="18" width="164" height="164" rx="4" fill="none" stroke="#3e1c08" strokeWidth="3" opacity="0.5" />

        {/* Hålet med ansiktet */}
        <path d={HOLE} fill={u("dark")} />
        <g clipPath={`url(#hole-${id})`}>
          <circle cx="100" cy="92" r="60" fill={u("light")} />
          <g transform="rotate(-10 100 96)">
            {/* Vildvuxet hår */}
            <path d="M56 70 C 58 44, 84 34, 104 36 C 126 38, 146 52, 144 76 C 138 64, 128 60, 118 62 C 112 54, 96 54, 88 62 C 78 58, 64 62, 56 70 Z" fill="#2e1a10" />
            <path d="M60 64 l -8 -6 M 72 50 l -4 -10 M 92 40 l 0 -10 M 116 40 l 6 -9 M 136 54 l 9 -6" stroke="#2e1a10" strokeWidth="4" strokeLinecap="round" />
            {/* Ansiktet */}
            <path d="M62 78 C 60 58, 80 52, 100 54 C 122 56, 140 64, 138 86 C 136 110, 124 134, 100 136 C 78 136, 64 112, 62 78 Z" fill={u("skin")} />
            {/* Rynkad panna */}
            <path d="M76 64 C 88 60, 108 60, 124 66 M 80 70 C 92 67, 106 67, 120 72" stroke="#b86a4a" strokeWidth="1.4" fill="none" opacity="0.8" />
            {/* Uppskjutna ögonbryn */}
            <path d="M70 80 C 76 70, 88 70, 94 78" stroke="#2e1a10" strokeWidth="5" strokeLinecap="round" fill="none" />
            <path d="M106 78 C 112 68, 126 68, 132 80" stroke="#2e1a10" strokeWidth="5" strokeLinecap="round" fill="none" />
            {/* Stirrande ögon med mörka ringar */}
            {[82, 119].map(ex => (
              <g key={ex}>
                <ellipse cx={ex} cy="89" rx="10" ry="8" fill="#9a5a44" opacity="0.4" filter={`url(#soft-${id})`} />
                <ellipse cx={ex} cy="88" rx="8" ry="6.4" fill="#ffffff" />
                <circle cx={ex + 1.5} cy="88" r="3" fill="#2a3a24" />
                <circle cx={ex + 1.5} cy="88" r="1.6" fill="#050505" />
                <circle cx={ex + 2.6} cy="86.6" r="0.9" fill="#ffffff" />
                <path d={`M${ex - 8.5} 86 C ${ex - 4} 81, ${ex + 4} 81, ${ex + 8.5} 86`} stroke="#6a3424" strokeWidth="1.6" fill="none" />
              </g>
            ))}
            {/* Näsa */}
            <path d="M100 90 C 98 100, 94 106, 96 110 C 99 112, 104 112, 107 109" stroke="#a45a3e" strokeWidth="2.4" fill="none" strokeLinecap="round" />
            <ellipse cx="97" cy="104" rx="3" ry="4" fill="#ffffff" opacity="0.35" />
            {/* Det galna flinet */}
            <path d="M74 114 C 86 122, 116 122, 128 112 C 124 128, 112 134, 100 134 C 86 134, 76 126, 74 114 Z" fill="#4a0c10" />
            <path d="M77 115 C 88 121, 114 121, 126 113 L 124 118 C 112 124, 88 124, 79 119 Z" fill="#ffffff" />
            <path d="M86 118.5 V 122 M 94 120 V 123.5 M 102 120.5 V 124 M 110 120 V 123.5 M 118 118 V 121.5" stroke="#d8d0c0" strokeWidth="1" />
            <path d="M88 130 C 96 127, 106 127, 114 129 C 108 133, 94 133, 88 130 Z" fill="#fffaf0" />
            <path d="M72 112 C 70 110, 71 107, 74 108 M 130 110 C 132 108, 131 105, 128 106" stroke="#a45a3e" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </g>
        </g>
        {/* Flisor runt hålet */}
        <path d={HOLE} fill="none" stroke="#f0b06a" strokeWidth="3" strokeLinejoin="miter" opacity="0.9" />
        <path d={HOLE} fill="none" stroke="#5e2e12" strokeWidth="1.2" transform="translate(1.5 1.5)" opacity="0.8" />
        <g fill="#e8a262" stroke="#6a3414" strokeWidth="0.8">
          <path d="M92 44 L 88 32 L 97 50 Z" />
          <path d="M148 70 L 162 66 L 144 78 Z" />
          <path d="M58 120 L 46 128 L 64 116 Z" />
          <path d="M124 134 L 128 148 L 118 130 Z" />
        </g>

        {/* Yxan som sitter kvar i dörren */}
        <g transform="rotate(-24 164 132)">
          <rect x="160" y="118" width="7" height="80" rx="3" fill="#6a3a1c" />
          <rect x="161.5" y="118" width="2" height="80" fill="#a8683c" opacity="0.7" />
          <path d="M146 108 C 150 100, 172 100, 178 108 L 176 128 C 170 124, 152 124, 148 128 Z" fill={u("steel")} />
          <path d="M146 108 C 150 100, 172 100, 178 108" stroke="#ffffff" strokeWidth="1.4" fill="none" opacity="0.9" />
        </g>

        {/* Texten i klassisk meme-stil */}
        <MemeText y={168} size={19} />
      </g>
      <circle cx="100" cy="100" r="96" fill="none" stroke="#2a1208" strokeWidth="2" opacity="0.6" />
      </g>
    </svg>
  );
};

const meme = (file: string) => `${process.env.PUBLIC_URL}/holidays/${file}`;

export const JOHNNY_PHOTOS = ["shining-heres-johnny.gif", "heres-johnny.gif", "heres-johnny.jpg"].map(meme);

/**
 * Memes som turas om med Johnny var tredje sekund, i den här ordningen:
 * Johnny → Voldemort → IT → Dr. Evil → Hannibal → Sauron → Katla → Dockan →
 * Palpatine → Johnny … Första gången Palpatine (FINAL_GUEST) visas blir han
 * kvar i 10 s och blixten slår ner i änkehuset. På andra varvet stannar den
 * på Palpatine.
 * Tom `lines` betyder ingen text.
 * x/y/size (eller w/h) och align styr beskärningen i 200-rutan. Bilden måste
 * täcka cirkeln (4–196) men hålla sig inom marginalen (ICON_PAD).
 */
export const EXTRA_MEMES: {
  src: string;
  lines: string[];
  guest: string;
  x?: number;
  y?: number;
  size?: number;
  w?: number;
  h?: number;
  align?: string;
}[] = [
  { src: meme("voldemort.gif"), lines: ["JAG MED"], guest: "Voldemort" },
  // IT lyfts lite så mer av ansiktet syns ovanför texten.
  { src: meme("IT.gif"), lines: ["KOM SÅ", "FIKAR VI"], guest: "It", x: -10, y: -22, size: 220 },
  // Dr. Evil sitter till höger i en bred bild: rutan sträcks åt vänster så
  // att ansiktet hamnar mitt i cirkeln och hela huvudet syns.
  { src: meme("dr-evil.gif"), lines: ["EN MILJON", "BULLAR"], guest: "Dr. Evil", x: -60, y: 3, w: 262, h: 230 },
  // Hannibal står i helfigur i en hög bild: förankra upptill och zooma in så
  // att ansiktet syns och undertexten nedtill hamnar utanför.
  {
    src: meme("hannibal.gif"),
    lines: ["JAG TAR MED", "HEMLAGAT"],
    guest: "Hannibal",
    x: -50,
    y: -15,
    w: 300,
    h: 275,
    align: "xMidYMin",
  },
  // Hela Sauron ryms i cirkeln.
  { src: meme("sauron.gif"), lines: ["JAG HAR ÖGA", "PÅ FIKAT"], guest: "Sauron", x: 2, y: 2, size: 196 },
  // Katla lyfts så att gapet syns ovanför texten.
  { src: meme("katla.gif"), lines: ["KATLA KOKAR", "KAFFE!"], guest: "Katla", x: -4, y: -14, size: 214 },
  // Dockan zoomar in på ansiktet; lyft så att ögonen syns ovanför texten.
  { src: meme("dockan.gif"), lines: ["JAG SITTER", "BREDVID DIG"], guest: "Dockan", x: -10, y: -24, size: 220 },
  { src: meme("palpatine.gif"), lines: ["JAG FIKADE", "FÖR LÄNGE"], guest: "Palpatine" },
];
const JOHNNY_GUEST = "Johnny";
/** Alla gästnamn, så att hjulet kan ge dem en gemensam storlek. */
export const MEME_GUEST_NAMES = [JOHNNY_GUEST, ...EXTRA_MEMES.map(m => m.guest)];
const SWAP_MS = 3000;
/** Bytet stannar när FINAL_GUEST visas på varv nummer STOP_ON_LOOP. */
const STOP_ON_LOOP = 2;
const FINAL_GUEST = "Palpatine";
const FINAL_INDEX = EXTRA_MEMES.findIndex(m => m.guest === FINAL_GUEST);
/**
 * Första varvets FINAL_GUEST blir kvar längre: blixten slår ner i änkehuset
 * STRIKE_AT_MS in i bilden, och FINAL_HOLD_MS efter att bilden kom startar
 * andra varvet.
 */
const STRIKE_AT_MS = 5000;
const FINAL_HOLD_MS = STRIKE_AT_MS + 5000;

/**
 * Finns det färre grupper än skurkar plockas skurkar bort i den här ordningen,
 * så att alla som visas får en egen grupp. Johnny (först) och Palpatine (sist)
 * är alltid med.
 */
const DROP_ORDER = ["Dockan", "Katla", "Sauron", "Hannibal", "Dr. Evil", "It", "Voldemort"];

/** Index i EXTRA_MEMES för de skurkar som får plats när hjulet har `groups` grupper. */
const memesForGroups = (groups: number | undefined) => {
  const all = EXTRA_MEMES.map((_, i) => i);
  if (groups === undefined) return all;
  const excess = 1 + EXTRA_MEMES.length - groups;
  const dropped = DROP_ORDER.slice(0, Math.max(0, excess));
  return all.filter(i => !dropped.includes(EXTRA_MEMES[i].guest));
};

/**
 * Hjulet lyssnar här på vilken meme som visas, och lägger till memens
 * namn ("Johnny", "Voldemort", "It", …) i en slumpad grupp första gången.
 * `groups` är antalet grupper i hjulet och styr vilka skurkar som är med.
 */
export const MemeGuestContext = React.createContext<{ addGuest: (name: string) => void; groups: number } | null>(
  null
);

/** Meme-texten i klassisk vit Impact med svart kant, två rader så den får plats i cirkeln. */
const MEME_LINES = ["JAG ÄR OCKSÅ", "BJUDEN!"];
const MemeText: React.FC<{ y: number; size: number; lines?: string[] }> = ({ y, size, lines = MEME_LINES }) => (
  <g fontFamily="Impact, 'Anton', 'Arial Black', sans-serif" fontWeight="900" fontSize={size} textAnchor="middle" letterSpacing="0.3">
    {lines.map((line, i) => {
      const ly = y - (lines.length - 1 - i) * size * 1.05;
      return (
        <React.Fragment key={line}>
          <text x="100" y={ly} fill="none" stroke="#000000" strokeWidth={size * 0.24} strokeLinejoin="round">
            {line}
          </text>
          <text x="100" y={ly} fill="#ffffff">
            {line}
          </text>
        </React.Fragment>
      );
    })}
  </g>
);

export const HeresJohnnyIcon: React.FC<IconProps> = props => {
  const id = useId().replace(/:/g, "");
  // Provar bilderna i tur och ordning; saknas alla visas den tecknade.
  const [source, setSource] = useState(0);
  // Turas om mellan Johnny (-1) och de extra memesen; de som inte laddar hoppas över.
  const [tick, setTick] = useState(0);
  const [broken, setBroken] = useState<number[]>([]);
  const guestCtx = useContext(MemeGuestContext);
  const active = memesForGroups(guestCtx?.groups);
  const rotation = [-1, ...active.filter(i => !broken.includes(i))];
  const shown = rotation[tick % rotation.length];
  // Laddar inte FINAL_GUEST fortsätter bytet som förut i stället för att stanna.
  const stopped = tick >= (STOP_ON_LOOP - 1) * rotation.length && shown === FINAL_INDEX;
  const firstFinal = shown === FINAL_INDEX && tick < rotation.length;
  useEffect(() => {
    if (rotation.length < 2 || stopped) return;
    const t = window.setTimeout(() => setTick(n => n + 1), firstFinal ? FINAL_HOLD_MS : SWAP_MS);
    return () => window.clearTimeout(t);
  }, [tick, rotation.length, stopped, firstFinal]);
  // Under första varvets sista bild slår en blixt ner i änkehuset i
  // bakgrunden (HalloweenScene). Händelsen skickas så att själva nedslaget,
  // efter att skärmen mörknat, kommer exakt STRIKE_AT_MS in i bilden.
  useEffect(() => {
    if (!firstFinal) return;
    const t = window.setTimeout(() => window.dispatchEvent(new Event(FINAL_STRIKE_EVENT)), STRIKE_AT_MS - STRIKE_BUILDUP_MS);
    return () => window.clearTimeout(t);
  }, [firstFinal]);
  const showingCartoon = source >= JOHNNY_PHOTOS.length;
  const onGuest = guestCtx?.addGuest;
  useEffect(() => {
    // Den tecknade reserven visar bara Johnny, så bara han bjuds in då.
    if (!onGuest || (showingCartoon && shown !== -1)) return;
    // Med en enda grupp får Palpatine den, inte Johnny.
    if (shown === -1 && (guestCtx?.groups ?? 2) < 2) return;
    onGuest(shown === -1 ? JOHNNY_GUEST : EXTRA_MEMES[shown].guest);
  }, [shown, showingCartoon, onGuest, guestCtx?.groups]);
  if (showingCartoon) return <HeresJohnnyCartoon {...props} />;
  return (
    <svg viewBox={ICON_VIEWBOX} overflow="hidden" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g className="wheel-reveal">
      <defs>
        <clipPath id={`photo-${id}`}>
          <circle cx="100" cy="100" r="96" />
        </clipPath>
        <linearGradient id={`fade-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="55%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#photo-${id})`}>
        <rect width="200" height="200" fill="#120804" />
        <image key={source} href={JOHNNY_PHOTOS[source]} x="6" y="-4" width="208" height="208" preserveAspectRatio="xMidYMid slice" onError={() => setSource(i => i + 1)} />
        {/* Alla bilderna ligger kvar så bytet tonar utan att blinka. Bortvalda
            skurkar ritas inte alls, så deras gifar laddas inte heller. */}
        {EXTRA_MEMES.map((meme, i) => active.includes(i) && (
          <image
            key={meme.src}
            href={meme.src}
            x={meme.x ?? -4}
            y={meme.y ?? 0}
            width={meme.w ?? meme.size ?? 208}
            height={meme.h ?? meme.size ?? 208}
            preserveAspectRatio={`${meme.align ?? "xMidYMid"} slice`}
            opacity={shown === i ? 1 : 0}
            style={{ transition: "opacity 0.4s ease" }}
            onError={() => setBroken(b => [...b, i])}
          />
        ))}
        <rect width="200" height="200" fill={`url(#fade-${id})`} />
        <MemeText y={168} size={19} lines={shown === -1 ? MEME_LINES : EXTRA_MEMES[shown].lines} />      </g>
      <circle cx="100" cy="100" r="96" fill="none" stroke="#2a1208" strokeWidth="2" opacity="0.6" />
      </g>
    </svg>
  );
};
